<template>
  <div id="my-support-ticket-list">
    <!-- Loading Overlay -->
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular
        indeterminate
        size="64"
        color="#0097d7"
      ></v-progress-circular>
    </v-overlay>

    <!-- Premium Header Card with Professional Design -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" class="no-gutters">
            <v-col cols="auto">
              <div class="header-icon-box">
                <v-icon size="48" color="white">mdi-ticket-outline</v-icon>
              </div>
            </v-col>
            <v-col cols="auto" class="ml-6">
              <h1 class="header-title">{{ PageTitle }}</h1>
              <p class="header-subtitle">{{ PageDescription }}</p>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="d-flex gap-2">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    large
                    color="white"
                    @click.prevent="refreshPageData"
                    class="action-btn"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="28">mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Refresh</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="75%">
      <my-support-ticket-create
        :addRecordPrompt="addRecordPrompt"
        @hideDialog="hideAddDialog"
        v-if="addRecordPrompt"
      ></my-support-ticket-create>
    </v-dialog>

    <v-dialog v-model="previewRecordPrompt" persistent max-width="75%">
      <my-support-ticket-preview
        :pageTitle="previewRecordPageTitle"
        :previewRecordFlag="previewRecordFlag"
        :recordData="selectedData"
        @hidePreviewRecordPrompt="hidePreviewRecordPrompt"
        v-if="previewRecordPrompt"
      ></my-support-ticket-preview>
    </v-dialog>

    <!-- Filter Card -->
    <v-card elevation="12" class="filter-card mb-6">
      <v-container class="pa-6">
        <v-row>
          <v-col cols="12">
            <h3 class="filter-title">
              <v-icon color="#0097d7" size="24" class="mr-2"
                >mdi-filter-outline</v-icon
              >
              Filter Support Tickets
            </h3>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" sm="12" lg="4" md="4">
            <v-autocomplete
              @change="getTypeDetails"
              :reduce="(option) => option.value"
              :loading="DepartmentIdOptionsLoading"
              :items="DepartmentIdOptions"
              :rules="DepartmentIdRules"
              v-model="DepartmentId"
              required
              outlined
              dense
              label="Select Department"
              prepend-inner-icon="mdi-office-building"
              clearable
              class="custom-input"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="12" lg="4" md="4">
            <v-autocomplete
              :reduce="(option) => option.value"
              :loading="TypeIdOptionsLoading"
              :items="TypeIdOptions"
              v-model="TypeId"
              outlined
              dense
              label="Select Type (Optional)"
              prepend-inner-icon="mdi-tag-multiple"
              clearable
              class="custom-input"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="12" lg="2" md="4" class="d-flex">
            <v-btn
              :disabled="!valid1"
              @click.prevent="searchForm"
              :loading="SearchFlag"
              color="#0097d7"
              large
              rounded
              block
              class="search-btn white--text font-weight-bold"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Loading State -->
    <v-container class="py-0" v-if="LoadingFlag">
      <v-row wrap>
        <v-col cols="12" md="4" v-for="n in 6" :key="n">
          <v-skeleton-loader type="card-avatar"></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-container>

    <!-- Empty State Card -->
    <v-card
      elevation="12"
      class="empty-state-card"
      v-if="!LoadingFlag && tableData1.length === 0"
    >
      <v-container class="pa-12 text-center">
        <v-icon size="100" color="#bdbdbd" class="mb-6"
          >mdi-inbox-outline</v-icon
        >
        <h2 class="mb-4" style="color: #757575">No Support Tickets</h2>
        <p class="mb-8" style="color: #999">
          No support tickets found. Create one to get started!
        </p>
      </v-container>
    </v-card>

    <!-- Data Table Card -->
    <v-card
      elevation="12"
      class="table-card mb-6"
      v-if="!LoadingFlag && tableData1.length > 0"
    >
      <v-container class="pa-6">
        <v-row align="center" class="mb-6">
          <v-col cols="auto">
            <v-icon color="#0097d7" size="28">mdi-list-box</v-icon>
          </v-col>
          <v-col cols="auto">
            <h2 class="table-title">
              {{ tableData1.length }} Support Tickets Found
            </h2>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search tickets"
              single-line
              hide-details
              class="search-field"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Download Buttons -->
        <v-row v-if="tableData1.length > 0" class="mb-6">
          <v-col>
            <json-excel
              v-if="
                tableData1.length > 0 &&
                tableOptions1.JsonDownloadButtonFlag == true
              "
              :name="tableOptions1.ExcelFileName"
              :data="tableData1"
              :fields="tableOptions1.JsonExcelFields"
            >
              <v-btn color="info" class="mr-4">
                <v-icon left>mdi-download</v-icon> Download Excel
              </v-btn>
            </json-excel>
            <v-btn
              v-if="ExcelDownloadFlag == true && ExcelFileFlag == false"
              @click.prevent="generateReportExcel"
              :loading="ExcelLoadingFlag"
              color="#ff7f50"
              class="white--text mr-4"
            >
              <v-icon left>mdi-file-pdf</v-icon> Generate Report
            </v-btn>
            <v-btn
              v-if="ExcelFileName != '' && ExcelFileFlag == true"
              :href="ExcelFileName"
              target="_blank"
              color="#2fb65a"
              class="white--text"
            >
              <v-icon left>mdi-download</v-icon> Download Report
            </v-btn>
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-row>
          <v-col cols="12">
            <v-data-table
              class="support-tickets-table elevation-0"
              loading-text="Loading... Please wait"
              v-model="selected"
              :search="search"
              :loading="TableLoadingFlag"
              :item-key="tableOptions1.ItemKey"
              :single-select="tableOptions1.SingleSelectFlag"
              :headers="tableColumns1"
              :items="tableData1"
              :items-per-page="tableOptions1.ItemsPerPage"
              :footer-props="tableOptions1.FooterProps"
              :hide-default-footer="tableOptions1.HideDefaultFooter"
            >
              <template v-slot:item.ActiveStatusTxt="{ item }">
                <v-chip
                  :color="getActiveStatusColor(item.ActiveStatusTxt)"
                  dark
                  class="status-chip"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  {{ item.ActiveStatusTxt }}
                </v-chip>
              </template>

              <template v-slot:item.ProfilePic="{ item }">
                <v-avatar size="56" class="avatar-img">
                  <img :src="item.ProfilePic" :alt="item.Name" />
                </v-avatar>
              </template>

              <template v-slot:item.MonthlyEventsFlag="{ item }">
                <v-tooltip
                  bottom
                  v-if="item.MonthlyEventsFlag && item.Total > 0"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="item.MonthlyEventsFlag && item.Total > 0"
                      @click="editData(item, $event)"
                      target="_blank"
                      color="primary"
                      small
                      class="text-white action-icon-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-file-find</v-icon> Events
                    </v-btn>
                  </template>
                  <span>{{ item.MonthName }} Events</span>
                </v-tooltip>
              </template>

              <template v-slot:item.FileGenerateFlag="{ index, item }">
                <v-tooltip
                  bottom
                  v-if="item.FileGenerateFlag && item.Total > 0"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="item.MonthlyEventsFlag && item.Total > 0"
                      @click="generateReport(index, item)"
                      target="_blank"
                      color="red"
                      small
                      class="text-white action-icon-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-file-pdf</v-icon> Generate
                    </v-btn>
                  </template>
                  <span>Generate {{ item.MonthName }} MRF</span>
                </v-tooltip>
              </template>

              <template v-slot:item.FileDownloadFlag="{ item }">
                <v-tooltip
                  bottom
                  v-if="item.FileDownloadFlag && item.FilePath != ''"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="item.FilePath != '' && item.FileDownloadFlag == 1"
                      :href="item.FilePath"
                      target="_blank"
                      color="#2fb65a"
                      small
                      class="text-white action-icon-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-download</v-icon> Download
                    </v-btn>
                  </template>
                  <span>{{ item.MonthName }} MRF Download</span>
                </v-tooltip>
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="table-actions">
                  <v-tooltip bottom v-if="item.PreviewFlag">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        icon
                        size="24px"
                        color="#0097d7"
                        @click="previewData(item)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-icon"
                      >
                        mdi-file-search
                      </v-icon>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="item.EditFlag">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        icon
                        size="24px"
                        color="#ff9800"
                        @click="editData(item, $event)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-icon"
                      >
                        mdi-file-document-edit
                      </v-icon>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="item.DeleteFlag">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        icon
                        size="24px"
                        color="#f44336"
                        @click="deleteConfirmAlert(item, $event)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-icon"
                      >
                        mdi-delete
                      </v-icon>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </div>
</template>
    
    <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import MySupportTicketCreate from "@/view/pages/erp/my-support-ticket/MySupportTicketCreate.vue";
import MySupportTicketPreview from "@/view/pages/erp/my-support-ticket/MySupportTicketPreview.vue";

export default {
  mixins: [common],
  components: {
    MySupportTicketCreate,
    MySupportTicketPreview,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      TableLoadingFlag: false,
      OverlayLoadingFlag: false,

      DepartmentIdRules: [(v) => !!v || "Department is required"],
      DepartmentId: "",
      DepartmentIdOptions: [],
      DepartmentIdOptionsLoading: false,

      TypeId: "",
      TypeIdOptions: [],
      TypeIdOptionsLoading: false,

      rows: [],

      Modules: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",

      recordToEdit: "",
      record: {},
      rowData: {},
      addRecordPrompt: false,

      previewRecordFlag: false,
      previewRecordPrompt: false,
      previewRecordPageTitle: "",

      selectedData: {},

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() {
    /*
        this.$store.dispatch(SET_BREADCRUMB, [
          { title: "Vuetify", route: "alerts" },
          { title: "Form Inputs & Control", route: "autocompletes" },
          { title: "Fileinptus" },
        ]);
        */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        this.YearName = tr.CurrentJciYearName;
        this.getDepartmentDetails();
      }
    },

    DepartmentIdOptions: function () {
      this.DepartmentIdOptionsLoading = false;
    },

    TypeIdOptions: function () {
      this.TypeIdOptionsLoading = false;
    },
  },
  methods: {
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddDialog() {
      console.log("hideAddDialog called");
      this.addRecordPrompt = false;
      //   this.searchForm();
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    refreshPageData() {
      console.log("refreshPageData called");

      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "support_tickets",
        Action: "list_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getDepartmentDetails() {
      console.log("getDepartmentDetails called");
      this.DepartmentIdOptionsLoading = true;
      var selectbox1_source = "DepartmentId";
      var selectbox1_destination = "DepartmentIdOptions";
      var selectbox1_url = "api/department/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getTypeDetails() {
      console.log("getTypeDetails called");
      this.TypeIdOptionsLoading = true;
      var selectbox1_source = "TypeId";
      var selectbox1_destination = "TypeIdOptions";
      var selectbox1_url = "api/ticket-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        DepartmentId: this.DepartmentId,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },

    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
      this.searchForm();
    },
    searchForm() {
      console.log("searchForm is called");

      var message = "";

      // var validate1 = this.$refs.form2.validate();
      // console.log("validate1=" + validate1);

      this.LoadingFlag = true;
      this.tableData1 = [];
      this.ExcelDownloadFlag = false;
      this.ExcelFileName = "";
      this.ExcelFileFlag = false;
      this.Modules = {};

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/ticket/lists";
      var upload = {
        UserInterface: 1,
        DepartmentId: this.DepartmentId,
        TypeId: this.TypeId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var table = {};
      var successTxt = "";
      var errorTxt = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);
          successTxt = response.data.success;
          errorTxt = response.data.error;
          console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);
          console.log({ records });

          if (flag == 1) {
            var options = records.TableOptions;
            console.log({ options });
            thisIns.tableColumns1 = records.TableHeader;
            thisIns.tableOptions1 = options;
            thisIns.ExcelDownloadFlag = options.ExcelDownloadButtonFlag;
            thisIns.tableData1 = records.TableData;

            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.previewRecordFlag = true;
        this.previewRecordPageTitle = "My Ticket Preview";
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
    <style lang="scss">
// Color Palette - JCI Approved
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$text-90-black: #414042;

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

#my-support-ticket-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 16px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.5);
    }
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
    padding: 0;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
    margin: 6px 0 0 0;
  }

  .action-btn {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
    }

    .v-icon {
      transition: all 0.3s ease;
    }

    &:hover .v-icon {
      transform: scale(1.15);
    }
  }

  .filter-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 36px rgba(58, 103, 177, 0.1) !important;
    border: 2px solid rgba(0, 151, 215, 0.15) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .filter-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    margin: 0;
    letter-spacing: 0.5px;
  }

  .custom-input {
    min-height: 56px;
    &::v-deep {
      .v-field {
        background: #ffffff !important;
      }

      .v-field__outline {
        border-color: rgba(0, 151, 215, 0.2) !important;
        transition: all 0.3s ease !important;
      }

      &:hover .v-field__outline {
        border-color: rgba(0, 151, 215, 0.4) !important;
      }

      &.v-field--focused .v-field__outline {
        border-color: $aqua !important;
        border-width: 2px !important;
        box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.08) !important;
      }

      .v-field__prepend-inner {
        color: $aqua !important;
      }
    }
  }

  .search-btn {
    background: linear-gradient(135deg, $aqua 0%, #0086c4 100%) !important;
    box-shadow: 0 8px 20px rgba(0, 151, 215, 0.25) !important;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;

    &:hover {
      transform: translateY(-3px) !important;
      box-shadow: 0 12px 28px rgba(0, 151, 215, 0.35) !important;
    }

    &:active {
      transform: translateY(-1px) !important;
    }
  }

  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .search-field {
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
  }

  .support-tickets-table {
    &::v-deep {
      thead {
        tr {
          background: linear-gradient(
            135deg,
            #f5f7fa 0%,
            #e8f4f8 100%
          ) !important;
          border-bottom: 3px solid $aqua !important;

          th {
            font-size: 13px !important;
            font-weight: 800 !important;
            color: $navy !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            padding: 16px 12px !important;
            background: linear-gradient(
              135deg,
              #f5f7fa 0%,
              #e8f4f8 100%
            ) !important;

            .v-icon {
              color: $aqua !important;
            }

            &:hover {
              background: linear-gradient(
                135deg,
                #e8f4f8 0%,
                #dcedf5 100%
              ) !important;
            }
          }
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid #e0e0e0 !important;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;

          &:hover {
            background-color: #f0f7ff !important;
            box-shadow: inset 0 0 20px rgba(0, 151, 215, 0.1) !important;
            transform: scale(1.01) !important;
          }

          td {
            padding: 14px 12px !important;
            font-size: 13px !important;
            color: $text-off-black !important;
            vertical-align: middle !important;

            &:first-child {
              padding-left: 16px !important;
            }

            &:last-child {
              padding-right: 16px !important;
            }
          }
        }

        tr:nth-of-type(even) {
          background-color: #fafbfc !important;
        }
      }
    }
  }

  .status-chip {
    font-weight: 700 !important;
    letter-spacing: 0.3px !important;
    text-transform: uppercase !important;
    font-size: 11px !important;
    animation: slideDown 0.4s ease-out;
  }

  .avatar-img {
    border: 3px solid $aqua !important;
    box-shadow: 0 4px 12px rgba(0, 151, 215, 0.2) !important;
    transition: all 0.3s ease !important;

    &:hover {
      transform: scale(1.1) !important;
      box-shadow: 0 8px 20px rgba(0, 151, 215, 0.3) !important;
    }
  }

  .table-actions {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;

    .action-icon {
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      cursor: pointer;

      &:hover {
        transform: scale(1.3) rotate(5deg);
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
      }
    }
  }

  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    cursor: pointer !important;

    &:hover {
      transform: scale(1.15) !important;
      filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15)) !important;
    }
  }

  .empty-state-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .gap-2 {
    gap: 8px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .pa-8 {
    padding: 32px;
  }

  .pa-6 {
    padding: 24px;
  }

  .pa-12 {
    padding: 48px;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .no-gutters {
    margin-right: -12px;
    margin-left: -12px;

    > .v-col,
    > [class*="v-col-"] {
      padding-right: 12px;
      padding-left: 12px;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-card {
      margin-bottom: 20px;
    }

    .header-title {
      font-size: 28px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .header-icon-box {
      padding: 12px;

      .v-icon {
        font-size: 36px !important;
      }
    }

    .filter-title {
      font-size: 16px;
    }

    .table-title {
      font-size: 16px;
    }

    .support-tickets-table::v-deep {
      tbody tr td {
        padding: 10px 12px !important;
        font-size: 12px;
      }
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;

    .header-card {
      margin-bottom: 16px;
      border-radius: 16px;

      .header-content {
        padding: 12px !important;
      }
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 13px;
    }

    .header-icon-box {
      padding: 12px;

      .v-icon {
        font-size: 32px !important;
      }
    }

    .filter-card {
      margin-bottom: 16px;
      border-radius: 14px;
    }

    .table-card {
      margin-bottom: 16px;
      border-radius: 14px;
    }

    .support-tickets-table::v-deep {
      thead tr th {
        padding: 12px 8px !important;
        font-size: 11px !important;
      }

      tbody tr td {
        padding: 10px 8px !important;
        font-size: 11px;
      }
    }

    .table-actions {
      gap: 8px;

      .action-icon {
        font-size: 20px !important;
      }
    }

    .empty-state-card {
      padding: 20px 16px !important;

      .v-icon {
        font-size: 60px !important;
      }
    }
  }
}
</style>